import { useCallback, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  PlannedResource,
  useUpdateWorkAssignmentListAttachment,
} from 'components/IAP/hooks/assignmentListHooks';
import { useCreatePositionResource } from 'components/IAP/hooks/resourceInIncidentHooks';
import { NULL_VALUE } from 'constants/displayConstants';
import type { ResourceSelection } from './AssignmentCardResourceDialog';
import {
  ICS204_HINT,
  ICS204_MUTED,
  ICS204_OUTLINE_BTN,
  ICS204_TABLE,
} from './assignmentCardStyles';
import useRosterResourceOptions, {
  ROSTER_RESOURCE_KIND_LABELS,
  type RosterResourceOption,
} from './useRosterResourceOptions';

type AssignmentCardRosterDialogProps = {
  walaId: string | null;
  onClose: () => void;
  plannedResources: PlannedResource[] | null;
  /** See the matching prop on AssignmentCardResourceDialog. */
  onCommit?: (selection: ResourceSelection[]) => void;
};

const UNAVAILABLE_TITLE =
  'On the draft roster only. This seat gets a resource record when the operational period advances, and can be planned then.';

/**
 * Rows are selected by token rather than by resource id, because a vacant seat
 * has no resource until the picker mints one on submit.
 */
const RESOURCE_TOKEN = 'res:';
const POSITION_TOKEN = 'pos:';
const TOKEN_LENGTH = RESOURCE_TOKEN.length;

const optionToken = (option: RosterResourceOption) => {
  // An occupied seat resolves through its resource, so picking the position and
  // picking its occupant land on the same record.
  if (option.resourceInIncidentId !== null) {
    return `${RESOURCE_TOKEN}${option.resourceInIncidentId}`;
  }
  if (option.positionId !== null) {
    return `${POSITION_TOKEN}${option.positionId}`;
  }
  return null;
};

const DIALOG_SURFACE =
  'dark tw:max-w-[126rem] tw:border-[var(--border-primary)] tw:bg-black tw:text-foreground';

const AssignmentCardRosterDialog = ({
  walaId,
  onClose,
  plannedResources,
  onCommit,
}: AssignmentCardRosterDialogProps) => {
  const { options, isPending } = useRosterResourceOptions();
  const { mutate: update } = useUpdateWorkAssignmentListAttachment();
  const { mutateAsync: createPositionResource } = useCreatePositionResource();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selected, setSelected] = useState<Set<string>>(
    new Set(
      plannedResources
        ?.filter((resource) => !resource.isDocumentationEntry)
        .map((resource) => `${RESOURCE_TOKEN}${resource.id}`) || []
    )
  );

  const toggleSelected = useCallback((token: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(token)) {
        next.delete(token);
      } else {
        next.add(token);
      }
      return next;
    });
  }, []);

  /**
   * Turns the picked rows into resource ids, creating a resource for any seat
   * picked while it was still empty. Returns null if one of those fails, so a
   * half-resolved selection is never committed.
   */
  const resolveSelection = async (): Promise<ResourceSelection[] | null> => {
    // Keyed by every token a row could carry, not just its current one: a refetch
    // that resolves a seat's resource flips its token, and a selection made
    // beforehand should still know what it was called.
    const nameByToken = new Map(
      options.flatMap((option) => {
        const tokens: string[] = [];
        if (option.resourceInIncidentId !== null) {
          tokens.push(`${RESOURCE_TOKEN}${option.resourceInIncidentId}`);
        }
        if (option.positionId !== null) {
          tokens.push(`${POSITION_TOKEN}${option.positionId}`);
        }
        return tokens.map((token) => [token, option.label] as const);
      })
    );

    const resolved: ResourceSelection[] = [];
    const seenIds = new Set<string>();

    for (const token of selected) {
      let id: string;

      if (token.startsWith(RESOURCE_TOKEN)) {
        id = token.slice(TOKEN_LENGTH);
      } else {
        try {
          const resource = await createPositionResource(
            token.slice(TOKEN_LENGTH)
          );
          id = resource.id;
        } catch {
          return null;
        }
      }

      // A position and its occupant share a resource, so both rows can be
      // checked and still amount to one planned entry.
      if (seenIds.has(id)) continue;
      seenIds.add(id);
      resolved.push({ id, name: nameByToken.get(token) ?? '' });
    }

    return resolved;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const selection = await resolveSelection();
    setIsSubmitting(false);

    if (selection === null) return;

    if (onCommit) {
      onCommit(selection);
      onClose();
      return;
    }

    if (!walaId) return;

    // The PUT replaces the whole list, so anything already planned but not
    // represented in this picker has to be carried over untouched.
    const alreadyPlanned = plannedResources || [];
    const documentationEntries = alreadyPlanned.filter(
      (resource) => resource.isDocumentationEntry
    );
    const selectedIds = new Set(selection.map((resource) => resource.id));
    const newIds = selection
      .map((resource) => resource.id)
      .filter((id) => !alreadyPlanned.some((resource) => resource.id === id));

    update({
      walaId,
      plannedResources: [
        ...documentationEntries,
        ...newIds.map((id) => ({ id }) as PlannedResource),
        ...alreadyPlanned.filter(
          (resource) =>
            !resource.isDocumentationEntry && selectedIds.has(resource.id)
        ),
      ],
    });
    onClose();
  };

  const [searchText, setSearchText] = useState('');

  const filteredList = useMemo(() => {
    if (!searchText) return options;
    const lowerSearch = searchText.toLowerCase();
    return options.filter((option) =>
      [option.label, option.positionName, option.detail].some((value) =>
        value?.toLowerCase().includes(lowerSearch)
      )
    );
  }, [options, searchText]);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className={DIALOG_SURFACE}
        showCloseButton={false}
        data-testid="ics204-add-from-roster-modal"
      >
        <DialogHeader>
          <DialogTitle>Add From Roster</DialogTitle>
        </DialogHeader>
        <Input
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Search the roster by name, position, or contact..."
          className="tw:bg-black tw:text-foreground"
        />
        <p className={ICS204_HINT}>
          Pick a person to plan whoever holds a seat today, or pick the position
          itself to plan the seat even while it is unassigned.
        </p>
        <div className="tw:max-h-[60vh] tw:overflow-auto">
          <Table className={ICS204_TABLE}>
            <TableHeader>
              <TableRow className="tw:hover:bg-transparent">
                <TableHead className="tw:w-10 tw:text-[var(--text-weak)]" />
                <TableHead className="tw:text-[var(--text-weak)]">
                  Name
                </TableHead>
                <TableHead className="tw:text-[var(--text-weak)]">
                  Position
                </TableHead>
                <TableHead className="tw:text-[var(--text-weak)]">
                  Contact
                </TableHead>
                <TableHead className="tw:text-[var(--text-weak)]">
                  Type
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className={ICS204_MUTED}>
                    {isPending
                      ? 'Loading the roster…'
                      : options.length === 0
                        ? 'Nobody is on the roster yet.'
                        : 'Nothing on the roster fits your search.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredList.map((option) => {
                  const token = optionToken(option);
                  const unavailable = token === null;
                  return (
                    <TableRow key={option.key}>
                      <TableCell>
                        <Checkbox
                          data-testid="ics204-roster-option-checkbox"
                          checked={token !== null && selected.has(token)}
                          disabled={unavailable}
                          title={unavailable ? UNAVAILABLE_TITLE : undefined}
                          aria-label={`Select ${option.label}`}
                          onCheckedChange={() =>
                            token !== null && toggleSelected(token)
                          }
                        />
                      </TableCell>
                      <TableCell
                        className="tw:text-foreground"
                        data-testid="ics204-roster-option-name"
                        title={unavailable ? UNAVAILABLE_TITLE : undefined}
                      >
                        {option.label}
                        {unavailable ? (
                          <span className={`${ICS204_MUTED} tw:ms-1`}>
                            (draft roster only)
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell
                        className="tw:text-foreground"
                        data-testid="ics204-roster-option-position"
                      >
                        {option.positionName ?? '—'}
                      </TableCell>
                      <TableCell className="tw:text-foreground">
                        {option.detail || NULL_VALUE}
                      </TableCell>
                      <TableCell
                        className="tw:text-foreground"
                        data-testid="ics204-roster-option-kind"
                      >
                        {ROSTER_RESOURCE_KIND_LABELS[option.kind]}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        <DialogFooter>
          <Button
            type="button"
            data-testid="ics204-add-from-roster-submit"
            disabled={isSubmitting}
            onClick={() => void handleSubmit()}
          >
            {isSubmitting ? 'Adding…' : 'Add Selected'}
          </Button>
          <Button
            type="button"
            variant="outline"
            className={ICS204_OUTLINE_BTN}
            onClick={onClose}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignmentCardRosterDialog;
